import { CONTENT_TYPES } from 'core/variables/constant';
import { JOB_STATUS } from 'core/variables/jobs';

import type { JobGradeFields, JobScore } from 'app/types/jobs';

// Con highest/lowest el backend calcula attempt_for_grade y score_for_grade con los intentos anteriores
// aunque el actual no esté corregido (reintento pendiente o entrega sin revisar). Usarlos ahí daba nota
// a un reintento pendiente, que se podía publicar y cerraba el reintento, y abría un intento antiguo
// en vez de la entrega por corregir. Hasta que el profesor corrige, cuenta el intento actual.
function isJobEvaluated(job: JobGradeFields | null | undefined): boolean {
  return job?.status === JOB_STATUS.EVALUATED;
}

// Los endpoints V2 mandan la nota como número o como string decimal ("10.00"); un string vacío o
// con solo espacios no es un 0 (Number('') === 0), así que se descarta antes de convertir.
function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Nota que cuenta para la calificación según la política de intentos del curso. Sin
 * `score_for_grade` válido (V3 anterior a LUC-89, política `last`) o con el job sin corregir,
 * devuelve `score` tal cual.
 */
export function getJobScoreForGrade(job: JobGradeFields | null | undefined): JobScore | undefined {
  const scoreForGrade = isJobEvaluated(job) ? toFiniteNumber(job?.score_for_grade) : null;
  return scoreForGrade ?? job?.score;
}

/**
 * Índice, en la lista cronológica de intentos, del que cuenta para la nota. Si el backend no lo
 * indica, apunta fuera de la lista o el job está sin corregir, el último, que es lo que se abría
 * antes de LUC-87.
 */
export function getAttemptIndexForGrade(job: JobGradeFields | null | undefined, attemptsCount: number): number {
  const lastIndex = attemptsCount - 1;
  const index = isJobEvaluated(job) ? toFiniteNumber(job?.attempt_for_grade) : null;
  return Number.isInteger(index) && index >= 0 && index <= lastIndex ? index : lastIndex;
}

export function withScoreForGrade<T extends JobGradeFields>(job: T): T {
  return { ...job, score: getJobScoreForGrade(job) };
}

/** SCORM y xAPI son los únicos tipos cuya nota provisional pinta el cuaderno antes de corregirlos. */
export function hasProvisionalGradebookScore(contentTypeGuid: string | null | undefined): boolean {
  return contentTypeGuid === CONTENT_TYPES.SCORM || contentTypeGuid === CONTENT_TYPES.XAPI;
}

/**
 * Nota que pinta el cuaderno del profesor (LUC-85). A diferencia de `getJobScoreForGrade`, un
 * SCORM/xAPI sin corregir pinta la nota que cuenta, porque su nota provisional ya se muestra hoy;
 * el resto, sin corregir, no tiene nota.
 */
export function getGradebookJobScore(job: JobGradeFields | null | undefined, shouldShowProvisionalScore: boolean): number | null {
  if (!isJobEvaluated(job) && !shouldShowProvisionalScore) {
    return null;
  }
  return toFiniteNumber(job?.score_for_grade) ?? toFiniteNumber(job?.score);
}
